import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { Sparkle } from "../controls";
import { useSheetCountRegistration } from "../../components/sheetCounts";
import { PlusIcon, TrashIcon } from "../labInvestigations/labIcons";
import { chipFor, ClockIcon, PencilIcon } from "./scheduleIcons";
import DailyScheduleService from "./DailyScheduleService";
import EntryModal from "./EntryModal";
import HydrationSleepCard from "./HydrationSleepCard";
import PastSchedulesMenu from "./PastSchedulesMenu";
import ScheduleCalendar from "./ScheduleCalendar";
import useFilledDays from "./useFilledDays";
import DeleteConfirmDialog from "../common/DeleteConfirmDialog";
import { formatDateDMY, formatTimeAmPm, normalizeActivity, todayDate } from "./data";
import { DS_V2_REFRESH } from "./events";
import { useCardDraft } from "../common/useCardDraft";

/**
 * Daily schedule — content column, backed by api-daily-schedule-v2-service.
 *
 * The store is ONE DOCUMENT PER PATIENT PER CALENDAR DAY, mirroring the
 * patient app's "Report routine" screen (one report per date). The header
 * is constant across states — title, "+ Add/Edit schedule" (a month
 * calendar: pick the day to build or edit — selected / filled / not filled
 * / future tiles) and "Past schedules ▾" (the filled days, paged). Both are
 * fed by ONE fetch of the last five years of filled days (useFilledDays),
 * which also drives the nothing-on-record gate.
 *
 * States, in the order a fresh patient meets them:
 *
 *   A. Nothing on record (no day has a document, today unfilled) — a single
 *      placeholder card: clock medallion, "No daily schedule yet", one
 *      "Add schedule" CTA. "Past schedules" renders disabled.
 *
 *   B. BUILDER (draft) — opened by "Add schedule", by picking an EMPTY day
 *      in the Add/Edit calendar, or by the banner pencil (a filled day
 *      picked in the calendar opens in view mode first, like today). The list is the day's saved rows (ticked, keeping
 *      their ids) merged with the default 14-row template (UNTICKED): a
 *      template row is disabled until the clinician ticks it, which is also
 *      what makes a row editable (click the activity → EntryModal, local
 *      commit). Every row carries a trash icon that drops it from the
 *      draft — behind the shared "Delete this entry?" confirmation, since a
 *      saved row trashed here is gone for good once Save lands. The banner
 *      reads "Adding/Editing schedule for <date>" and
 *      holds Add new entry / Cancel / Save. Save POSTs the ticked rows in
 *      one batch for that date — with `entryId` on the rows that already
 *      existed, so the server reconciles by id (update in place, soft-delete
 *      what was unticked or trashed, add the rest) and every row's audit
 *      trail survives. Cancel discards the draft. Nothing touches the
 *      server before Save.
 *
 *   C. Unfilled day, but the patient has other days — a small empty state
 *      for that date with an "Add schedule" CTA into the builder. On load
 *      the section skips this for today: if today has no schedule it opens
 *      on the latest day that does (state D), and today is one "Back to
 *      today" away.
 *
 *   D. VIEW — the day has saved rows. Banner "Schedule for <date>" with a
 *      pencil; rows read-only, each with a ticked box that is NOT
 *      flippable (it is what was saved). Nothing on a row edits — only the
 *      pencil (or the header calendar) opens the builder.
 *
 * Backend enforces the per-day no-duplicate rule (time OR activity name)
 * plus optimistic locking; a 409 hands back the live day (`current`) and
 * we rebase on it.
 *
 * Presentation is px-matched to "Card · Daily schedule (typical weekday)"
 * (3578:2976): the ds3-* classes, the row chips, the split time block. The
 * builder/view chrome (header pair, banner, tick + trash columns, disabled
 * rows, calendar) and the placeholder are ds6-*, from the Sep-2026 mocks.
 */

// Build a synthetic ScheduleEntry-shaped row from a staged "add"
// captured item so it can slot into the same timeline as persisted
// rows. Wave C first-cut merges only staged ADDS.
const stagedAddToEntryRow = (item) => {
  const p = (item && item.current) || {};
  let timeMinutes = 0;
  if (typeof p.timeMinutes === "number") timeMinutes = p.timeMinutes;
  else if (typeof p.time_minutes === "number") timeMinutes = p.time_minutes;
  return {
    entryId:       `staged-${item.id}`,
    timeMinutes,
    activity:      p.activity || p.value || "(untitled)",
    _captured:     item,
    _capturedIsAi: item.source === "ai",
    _isRejected:   item.state === "rejected",
    _stagedAdd:    true,
  };
};

// "Filled" is about the doc existing (a hydration-only save creates one);
// what the timeline cares about is whether the day has saved rows.
const hasTimeline = (d) => !!(d && d.filled && d.entries && d.entries.length > 0);

// Draft rows carry `enabled`; server rows and captured rows don't, and
// count as enabled (they are what the day already has).
const isEnabled = (e) => e.enabled !== false;

// Draft ids: rows that came from the server keep their ObjectID hex; rows
// born in the draft get a `local-…` id that must never reach the wire.
const LOCAL_ID = /^local-/;
const isSavedId = (id) => !!id && !LOCAL_ID.test(id);

const activityKey = (a) => normalizeActivity(a).toLowerCase();

const EMPTY_DOC = (date) => ({
  date,
  filled: false,
  entries: [],
  hydration: {},
  sleep: {},
  version: 0,
});

const DailySchedule = ({ patient, capturedItems, draftKey }) => {
  const patientId = patient && (patient.id || patient.patientID);

  // ZY-10356 Phase 1 — transcript-mode draft preservation across
  // transcript switches. CapturedSections passes a transcript-scoped
  // draftKey; standalone leaves it undefined and the hook no-ops.
  const [draft, saveDraft] = useCardDraft(draftKey);
  // Latch: a hydrated-draft mount must not have its localEntries wiped
  // by the server-doc load() below (which clears the draft on fresh
  // day loads). Cleared once we've honored the hydration.
  const hydratedFromDraftRef = useRef(!!(draft && "localEntries" in draft));

  // Selected calendar day — the clinician's local "today" on mount, then
  // (once the filled-days fetch is in) the LATEST day that has a schedule
  // if today has none: the section opens on the most recent routine rather
  // than an empty today. Done once per patient — after that the calendar /
  // Past schedules / "Back to today" drive it. The AI-transcript embed
  // keeps today: its captures land on today, so that is what it must show.
  const [date, setDate] = useState(() => (
    draft && "date" in draft ? draft.date : todayDate()
  ));
  const landedRef = useRef(null); // patientId the landing was done for

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null); // e.g. "updated elsewhere"
  const [modalTarget, setModalTarget] = useState(null); // { entry? }
  const [deleteTarget, setDeleteTarget] = useState(null); // draft row awaiting confirm

  // Draft rows — only meaningful while the builder is open. Shape is
  // {entryId, timeMinutes, activity, enabled}: saved rows and rows the
  // clinician adds start enabled:true, template rows enabled:false.
  // Nothing here hits the server until Save fires. Preserved across
  // background refreshes (e.g. after a hydration/sleep save that emits
  // DS_V2_REFRESH) so in-progress edits aren't wiped; dropped when the
  // DATE changes.
  const [localEntries, setLocalEntries] = useState(() => (
    draft && "localEntries" in draft ? draft.localEntries : []
  ));
  const [savingBatch, setSavingBatch] = useState(false);
  const [batchError, setBatchError] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  // Ref: the date the draft belongs to, so the load handler can tell "new
  // day selected" from a plain background refresh of the same day.
  // ZY-10356 Phase 1 — pre-seed with `date` when we hydrated from a
  // persisted draft, so load()'s "new day" branch doesn't clear the
  // in-progress localEntries on mount.
  const draftForRef = useRef(hydratedFromDraftRef.current ? date : null);

  // Bumped after every successful save so the calendar and the
  // nothing-on-record probe refetch.
  const [daysTick, setDaysTick] = useState(0);

  // The filled days of the last five years — one fetch, refetched on
  // daysTick. Feeds the Add/Edit calendar, the Past schedules list and the
  // "nothing logged yet" gate below (daysTotal: null until loaded, -1 on
  // failure so the gate never hides the column on an unknown).
  const filled = useFilledDays(patientId, daysTick);
  const daysTotal = filled.days ? filled.days.length : filled.status === "error" ? -1 : null;

  const inTranscriptMode = !!(capturedItems && capturedItems.length > 0);
  useEffect(() => {
    if (!patientId || !filled.days || landedRef.current === patientId) return;
    landedRef.current = patientId;
    if (inTranscriptMode) return;
    const today = todayDate();
    if (!filled.set.has(today) && filled.days.length > 0) {
      setDate(filled.days[0].date); // newest first
    }
  }, [patientId, filled.days, filled.set, inTranscriptMode]);
  // The builder is open for the selected day. Set by "Add schedule" / a
  // calendar pick / the pencil; cleared by Cancel, by a successful Save,
  // and by moving to another day (a draft belongs to its day).
  const [building, setBuilding] = useState(() => (
    draft && "building" in draft ? draft.building : false
  ));
  // A calendar pick moves the section to that day AND opens the builder —
  // but the builder seeds from the day's saved rows, so it must wait for
  // that day's doc to land. This holds the date until then.
  const [buildOn, setBuildOn] = useState(() => (
    draft && "buildOn" in draft ? draft.buildOn : null
  ));

  // ZY-10356 Phase 1 — sync persistent draft fields back to the modal-
  // root store on every change. Server-fetched state (doc, loading)
  // and ephemeral UI state (modalTarget, deleteTarget, savingBatch,
  // batchError, loadingTemplate, daysTick, notice) are deliberately
  // excluded — those re-derive on mount.
  useEffect(() => {
    saveDraft({ date, localEntries, building, buildOn });
  }, [saveDraft, date, localEntries, building, buildOn]);

  const load = useCallback(() => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    const svc = new DailyScheduleService();
    svc.list(
      patientId,
      date,
      (res) => {
        setDoc(res || EMPTY_DOC(date));
        if (draftForRef.current !== date) {
          // New day → any draft left over from the previous one goes.
          setLocalEntries([]);
          setBatchError(null);
        }
        // Otherwise (background refresh of the same day, e.g. after a
        // hydration save) — leave the draft alone; in-progress edits stay.
        draftForRef.current = date;
        setLoading(false);
      },
      (err) => {
        setError((err && err.message) || "Failed to load daily schedule");
        setLoading(false);
      }
    );
  }, [patientId, date]);

  useEffect(() => {
    load();
    EventService.addListener(DS_V2_REFRESH, load);
    return () => EventService.removeListener(DS_V2_REFRESH, load);
  }, [load]);

  // A draft is per-day: leaving the day closes the builder. Guarded so
  // a hydrated-draft mount (ZY-10356 Phase 1) doesn't flip building
  // off on first render — the effect only reacts to a REAL date change
  // after the component has settled.
  const prevDateRef = useRef(date);
  useEffect(() => {
    if (prevDateRef.current !== date) {
      setBuilding(false);
    }
    prevDateRef.current = date;
  }, [date]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const version = (doc && doc.version) || 0;
  const serverEntries = useMemo(() => (doc && doc.entries) || [], [doc]);
  const dayHasRows = hasTimeline(doc);

  // A 409 carries the live day. Rebase the doc on it and tell the user;
  // the draft is kept so nothing they typed is lost.
  const rebaseOnConflict = (err) => {
    if (err && err.reason === "version" && err.current) {
      setDoc(err.current);
      setNotice("This day was updated elsewhere — showing the latest version.");
      setDaysTick((t) => t + 1);
      return true;
    }
    return false;
  };

  // ── Draft (builder) ───────────────────────────────────────────────────
  //
  // Local IDs — draft rows need a stable-per-render key for the checkbox,
  // modal and trash button to target them. Rows born in the draft keep a
  // local id until the batch save assigns real ObjectIDs.
  const nextLocalId = useCallback(
    () => `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    []
  );

  // Seed the draft: the day's saved rows (ticked, ids kept) plus every
  // template row that clashes with none of them (unticked). A template row
  // that shares a saved row's minute or name is left out — it could never
  // be ticked anyway, and the saved row already stands for it.
  const openDraft = useCallback(
    (templateRows) => {
      const saved = serverEntries.map((e) => ({
        entryId: e.entryId,
        timeMinutes: e.timeMinutes,
        activity: e.activity,
        enabled: true,
      }));
      const takenTimes = new Set(saved.map((e) => e.timeMinutes));
      const takenNames = new Set(saved.map((e) => activityKey(e.activity)));
      const fromTemplate = (templateRows || [])
        .filter((r) => !takenTimes.has(r.timeMinutes) && !takenNames.has(activityKey(r.activity)))
        .map((r) => ({
          entryId: nextLocalId(),
          timeMinutes: r.timeMinutes,
          activity: r.activity,
          enabled: false,
        }));
      setLocalEntries([...saved, ...fromTemplate]);
    },
    [serverEntries, nextLocalId]
  );

  const openBuilder = () => {
    if (loadingTemplate) return;
    setBuilding(true);
    setBatchError(null);
    setLoadingTemplate(true);
    const svc = new DailyScheduleService();
    svc.template(
      (rows) => {
        setLoadingTemplate(false);
        openDraft(rows);
      },
      (err) => {
        setLoadingTemplate(false);
        // The saved rows are still editable without the template.
        openDraft([]);
        setBatchError((err && err.message) || "Could not load the default timeline");
      }
    );
  };

  // Calendar pick. A day that already has a schedule opens in VIEW mode —
  // same as today: banner + pencil, and the pencil is how you edit. Only
  // a day with nothing on it goes straight into the builder (there is
  // nothing to view). Moving first, building once that day's doc is in.
  const pickDayToBuild = (key) => {
    const hasSchedule = filled.set.has(key);
    if (key === date) {
      if (!hasSchedule) openBuilder();
      return;
    }
    if (!hasSchedule) setBuildOn(key);
    setDate(key);
  };
  const openBuilderRef = useRef(openBuilder);
  openBuilderRef.current = openBuilder;
  useEffect(() => {
    if (!buildOn || loading || !doc || doc.date !== buildOn) return;
    setBuildOn(null);
    openBuilderRef.current();
  }, [buildOn, loading, doc]);

  const commitLocalEntry = ({ entryId, timeMinutes, activity }) => {
    setLocalEntries((prev) => {
      if (entryId) {
        return prev.map((e) =>
          e.entryId === entryId ? { ...e, timeMinutes, activity } : e
        );
      }
      // A row the clinician typed in is wanted — it starts ticked.
      return [...prev, { entryId: nextLocalId(), timeMinutes, activity, enabled: true }];
    });
  };

  // Tick / untick a draft row. Ticking on runs the same per-day duplicate
  // rule the server applies to the batch (same time OR same normalised
  // activity among the rows that will be saved), so the clash is caught
  // here instead of as a failed Save.
  const toggleLocalEntry = (entryId) => {
    const target = localEntries.find((e) => e.entryId === entryId);
    if (!target) return;
    if (!isEnabled(target)) {
      const key = activityKey(target.activity);
      const clash = localEntries.find(
        (e) =>
          e.entryId !== entryId &&
          isEnabled(e) &&
          (e.timeMinutes === target.timeMinutes || activityKey(e.activity) === key)
      );
      if (clash) {
        setNotice(
          `Can't include "${target.activity}" — ${formatTimeAmPm(clash.timeMinutes)} ${
            clash.activity
          } already uses that time or name. Edit one of them first.`
        );
        return;
      }
    }
    setLocalEntries((prev) =>
      prev.map((e) => (e.entryId === entryId ? { ...e, enabled: !isEnabled(e) } : e))
    );
  };

  const removeLocalEntry = (entryId) => {
    setLocalEntries((prev) => prev.filter((e) => e.entryId !== entryId));
  };

  // Trash → "Delete this entry?" (board 3577:2861) → drop the row from the
  // draft. Still local: the day only changes when Save lands.
  const confirmDelete = () => {
    if (!deleteTarget) return;
    removeLocalEntry(deleteTarget.entryId);
    setDeleteTarget(null);
  };

  const cancelDraft = () => {
    setLocalEntries([]);
    setBatchError(null);
    setBuilding(false);
    setDeleteTarget(null);
  };

  const enabledDraft = localEntries.filter(isEnabled);

  // Same rule the server applies to the batch, run before the round-trip
  // so a clash is reported on the Save click, naming the rows.
  const draftClash = (rows) => {
    const byTime = new Map();
    const byName = new Map();
    for (const e of rows) {
      const t = byTime.get(e.timeMinutes);
      if (t) {
        return `Two entries share the time ${formatTimeAmPm(e.timeMinutes)} — "${t.activity}" and "${e.activity}". Change one of them before saving.`;
      }
      byTime.set(e.timeMinutes, e);
      const k = activityKey(e.activity);
      const n = byName.get(k);
      if (n) {
        return `"${e.activity}" appears twice (${formatTimeAmPm(n.timeMinutes)} and ${formatTimeAmPm(e.timeMinutes)}). Remove or rename one before saving.`;
      }
      byName.set(k, e);
    }
    return null;
  };

  const saveBatch = () => {
    if (savingBatch || enabledDraft.length === 0) return;
    const clash = draftClash(enabledDraft);
    if (clash) {
      setBatchError(clash);
      return;
    }
    setSavingBatch(true);
    setBatchError(null);
    const body = {
      // Saved rows travel with their id so the server keeps them (and
      // their history); draft-born rows go without one and are added.
      entries: enabledDraft.map((e) => ({
        ...(isSavedId(e.entryId) ? { entryId: e.entryId } : {}),
        timeMinutes: e.timeMinutes,
        activity: e.activity,
      })),
      expectedVersion: version || 0,
    };
    const svc = new DailyScheduleService();
    svc.batchReplaceEntries(
      patientId,
      date,
      body,
      () => {
        setSavingBatch(false);
        setBuilding(false);
        setLocalEntries([]);
        setDaysTick((t) => t + 1);
        EventService.emit(DS_V2_REFRESH);
      },
      (err) => {
        setSavingBatch(false);
        if (rebaseOnConflict(err)) return;
        setBatchError((err && err.message) || "Save failed — please retry");
      }
    );
  };

  // ── Rows to show ──────────────────────────────────────────────────────
  //
  // Builder open → the draft. Otherwise the saved rows, plus (transcript
  // embed only) the staged ADDS captured in the call. Wave C first-cut
  // merges only staged ADDS; standalone (`capturedItems` undefined) is a
  // no-op.
  const builderOpen = building || localEntries.length > 0;

  // Publish the open-builder / draft-rows state to the shell-level
  // SheetCountsProvider (ZY-10312). Treats an open builder (with or
  // without staged rows) as a "1" so the sidebar badge lights up on
  // Daily schedule and the leave-page guard flags it on in-app nav /
  // beforeunload. Content-only section with no editor rail, so without
  // this registration an unsaved draft would silently disappear on
  // tab switch with no warning.
  useSheetCountRegistration("daily-schedule", builderOpen ? 1 : 0);
  const displayEntries = useMemo(() => {
    const base = builderOpen ? localEntries : serverEntries;
    if (!inTranscriptMode) return base;
    const stagedAdds = [];
    for (const it of capturedItems) {
      if (!it) continue;
      // Soft-deleted (DB-update reject) — skip so row loses its staged
      // annotation after Reject.
      if (it.deletedAt) continue;
      // ZY-10356 Phase 6 — include new-entry rejects for strikethrough.
      {
        const isCommitted = it.state === "added_in_call" || it.state === "edited_in_call";
        const isNewReject = it.state === "rejected" && !it.dbRecordRef && it.lastCommitted;
        if (!isCommitted && !isNewReject) continue;
      }
      if (it.operation === "add") stagedAdds.push(stagedAddToEntryRow(it));
    }
    return [...base, ...stagedAdds];
  }, [builderOpen, localEntries, serverEntries, capturedItems, inTranscriptMode]);

  if (!patientId) {
    return <div className="ph-slot-empty"><p>Waiting for patient…</p></div>;
  }
  if (error) {
    return (
      <div className="ph-slot-empty">
        <p>{error}</p>
        <button type="button" className="sec-btn sec-btn-ghost" onClick={load}>
          Retry
        </button>
      </div>
    );
  }
  const dayFilled = !!(doc && doc.filled);
  // Hold the loading state until we can tell placeholder from column — an
  // unfilled day needs the /days probe back too, or the column would flash
  // in and then collapse into the placeholder.
  if (!doc || (!dayFilled && daysTotal === null)) {
    return <div className="ph-slot-empty"><p>Loading daily schedule…</p></div>;
  }

  const isToday = date === todayDate();

  // State A — nothing on record for this patient at all, and nothing
  // drafted locally or captured from the call that the column would need
  // to show. Pinned to today: the only way onto another date is the
  // calendar, and the placeholder's calendar is disabled.
  const showPlaceholder =
    !dayFilled &&
    daysTotal === 0 &&
    isToday &&
    !builderOpen &&
    !inTranscriptMode;

  if (showPlaceholder) {
    return (
      <div className="sec-stack">
        <div className="ds3-card ds6-placeholder">
          <header className="ds3-head">
            <h2 className="ds3-title">Daily schedule</h2>
            <div className="ds6-head-actions">
              <PastSchedulesMenu
                days={filled.days}
                status={filled.status}
                onRetry={filled.reload}
                date={date}
                onSelect={setDate}
                disabled
              />
            </div>
          </header>
          <div className="cc-empty">
            <span className="cc-empty-medallion" aria-hidden="true"><ClockIcon /></span>
            <p className="cc-empty-title">No daily schedule yet</p>
            <p className="cc-empty-hint">
              Nothing has been logged yet. Build the patient&rsquo;s daily
              routine to start tracking it.
            </p>
            <button type="button" className="cc-empty-cta" onClick={openBuilder}>
              <PlusIcon /> Add schedule
            </button>
          </div>
        </div>
      </div>
    );
  }

  const canSaveBatch = builderOpen && !savingBatch && enabledDraft.length > 0;
  const showEmpty = displayEntries.length === 0;
  const dateLabel = formatDateDMY(date);

  return (
    <div className="sec-stack">
      {notice && <div className="fam-warn ds5-notice">{notice}</div>}

      {/* Timeline card, px-matched to "Card · Daily schedule (typical
          weekday)" (3578:2976); header pair + banner from the mocks. */}
      <div className={`ds3-card${loading ? " is-loading" : ""}`} aria-busy={loading}>
        <header className="ds3-head">
          <h2 className="ds3-title">Daily schedule</h2>
          <div className="ds6-head-actions">
            <ScheduleCalendar
              date={date}
              filled={filled.set}
              onPick={pickDayToBuild}
              disabled={builderOpen}
              busy={loadingTemplate}
            />
            <PastSchedulesMenu
              days={filled.days}
              status={filled.status}
              onRetry={filled.reload}
              date={date}
              onSelect={setDate}
            />
          </div>
        </header>

        {builderOpen ? (
          /* Builder banner — "Adding schedule for 12 Sep 2026" with
             [+ Add new entry] [Cancel] [Save] flush right. */
          <div className="ds6-banner">
            <span className="ds6-banner-text">
              {dayHasRows ? "Editing" : "Adding"} schedule for {dateLabel}
            </span>
            <div className="ds6-banner-actions">
              <button
                type="button"
                className="ds6-banner-btn"
                onClick={() => setModalTarget({})}
              >
                <PlusIcon /> Add new entry
              </button>
              <button
                type="button"
                className="ds6-banner-btn"
                onClick={cancelDraft}
                disabled={savingBatch}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ds6-banner-save"
                onClick={saveBatch}
                disabled={!canSaveBatch}
                title={
                  enabledDraft.length === 0 && !savingBatch
                    ? "Tick at least one entry to save"
                    : undefined
                }
              >
                {savingBatch ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        ) : dayHasRows ? (
          /* View banner — "Schedule for 10 Jan 2026" + pencil. */
          <div className="ds6-banner is-view">
            <span className="ds6-banner-text">Schedule for {dateLabel}</span>
            <button
              type="button"
              className="ds6-banner-edit"
              onClick={openBuilder}
              disabled={loadingTemplate}
              aria-label="Edit this schedule"
              title="Edit this schedule"
            >
              <PencilIcon />
            </button>
          </div>
        ) : null}

        {batchError && (
          <div className="fam-warn is-error ds-batch-err">{batchError}</div>
        )}

        {showEmpty && builderOpen ? (
          <div className="fam-empty">
            {loadingTemplate ? (
              <p className="fam-empty-title">Loading the default timeline…</p>
            ) : (
              <>
                <p className="fam-empty-title">No entries in this draft</p>
                <span className="fam-empty-hint">
                  Add a new entry from the bar above, or Cancel to discard.
                </span>
              </>
            )}
          </div>
        ) : showEmpty ? (
          /* State C — this day is empty but the patient has other days. */
          <div className="cc-empty">
            <span className="cc-empty-medallion" aria-hidden="true"><ClockIcon /></span>
            <p className="cc-empty-title">
              No schedule for {isToday ? "today" : dateLabel}
            </p>
            <p className="cc-empty-hint">
              Nothing has been logged for this day. Build the routine from
              the default timeline or add entries one by one.
            </p>
            <button
              type="button"
              className="cc-empty-cta"
              onClick={openBuilder}
              disabled={loadingTemplate}
            >
              <PlusIcon /> {loadingTemplate ? "Loading…" : "Add schedule"}
            </button>
          </div>
        ) : (
          <div className="ds3-timeline">
            {[...displayEntries]
              .sort((a, b) => a.timeMinutes - b.timeMinutes)
              .map((e) => {
                // The board splits the time into digits + meridiem, centred
                // as one group in the 80×50 block. formatTimeAmPm already
                // renders "07:00 am", so it is split for layout only.
                const [clock, meridiem] = formatTimeAmPm(e.timeMinutes).split(" ");
                const chip = chipFor(e.activity);
                // Draft rows get the live tick + trash; saved rows in view
                // mode get a read-only tick (it is what was saved) and open
                // the builder when it is clicked. Captured rows get neither.
                const draft = builderOpen && !e._stagedAdd;
                const view = !builderOpen && !e._stagedAdd;
                const on = isEnabled(e);
                const rowClass = [
                  "ds3-row",
                  draft ? "is-draft" : "",
                  view ? "is-view" : "",
                  draft && !on ? "is-disabled" : "",
                  e._captured ? "is-captured" : "",
                  e._isRejected ? "is-captured-rejected" : "",
                ].filter(Boolean).join(" ");
                return (
                  <div className={rowClass} key={e.entryId}>
                    {draft && (
                      <input
                        type="checkbox"
                        className="ds3-check"
                        checked={on}
                        onChange={() => toggleLocalEntry(e.entryId)}
                        aria-label={`Include ${e.activity} at ${formatTimeAmPm(e.timeMinutes)}`}
                        title={on ? "Untick to leave this entry out" : "Tick to include and edit"}
                      />
                    )}
                    {view && (
                      /* Read-only: ticked because it was saved. It does not
                         flip and does not open anything — the pencil does. */
                      <input
                        type="checkbox"
                        className="ds3-check is-readonly"
                        checked
                        readOnly
                        tabIndex={-1}
                        aria-disabled="true"
                        onClick={(ev) => ev.preventDefault()}
                        aria-label={`${e.activity} at ${formatTimeAmPm(e.timeMinutes)} — saved`}
                        title="Use the pencil to edit this schedule"
                      />
                    )}
                    {e._stagedAdd && <span className="ds3-check-spacer" aria-hidden="true" />}
                    {draft && on ? (
                      <button
                        type="button"
                        className="ds3-time is-editable"
                        onClick={() => setModalTarget({ entry: e })}
                        title="Edit entry"
                        aria-label={`Edit ${e.activity}, ${formatTimeAmPm(e.timeMinutes)}`}
                      >
                        <span className="ds3-clock">{clock}</span>
                        {meridiem && <span className="ds3-ampm">{meridiem}</span>}
                      </button>
                    ) : (
                      <span className="ds3-time">
                        <span className="ds3-clock">{clock}</span>
                        {meridiem && <span className="ds3-ampm">{meridiem}</span>}
                      </span>
                    )}
                    <span
                      className="ds3-chip"
                      style={{ background: chip.bg, color: chip.fg }}
                      aria-hidden="true"
                    >
                      <chip.Icon />
                    </span>
                    {e._stagedAdd ? (
                      <span className="ds3-activity is-static" title="Captured in call — save to persist">
                        {e._capturedIsAi ? <span className="cc-spark" aria-label="AI captured" title="AI captured"><Sparkle aria-hidden /></span> : null}
                        {e.activity}
                      </span>
                    ) : draft && on ? (
                      <button
                        type="button"
                        className="ds3-activity"
                        onClick={() => setModalTarget({ entry: e })}
                        title="Edit entry"
                      >
                        {e.activity}
                      </button>
                    ) : (
                      <span
                        className="ds3-activity is-static"
                        title={draft ? "Tick the box to include and edit this entry" : undefined}
                      >
                        {e.activity}
                      </span>
                    )}
                    {draft && (
                      <button
                        type="button"
                        className="ds3-trash"
                        onClick={() => setDeleteTarget(e)}
                        aria-label={`Delete ${e.activity}`}
                        title="Delete entry"
                      >
                        <TrashIcon />
                      </button>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Card 2 — Hydration & sleep for the selected day */}
      <HydrationSleepCard
        patientId={patientId}
        date={date}
        doc={doc}
        onConflict={rebaseOnConflict}
        onSaved={() => setDaysTick((t) => t + 1)}
      />

      {modalTarget && (
        <EntryModal
          // Keying on the entry id forces a fresh mount when the caller
          // swaps Add → Edit; without it the useState initial values
          // (which read `entry` on first mount) never re-initialise.
          key={(modalTarget.entry && modalTarget.entry.entryId) || "new"}
          patientId={patientId}
          date={date}
          entry={modalTarget.entry}
          // Duplicate checks run against what the day will actually hold:
          // unticked draft rows are left out (they aren't saved).
          existingList={displayEntries.filter(isEnabled)}
          version={version}
          // Every edit goes through the draft; the modal never writes.
          stagingMode
          onLocalCommit={commitLocalEntry}
          onConflict={rebaseOnConflict}
          onClose={() => setModalTarget(null)}
          onSaved={() => setModalTarget(null)}
        />
      )}

      {/* Board 3577:2861 "Modal · Delete entry" — the shared, px-matched
          DeleteConfirmDialog; the only delta is its copy: time + em-dash +
          activity as the item name, "the daily routine" as the list. */}
      {deleteTarget && (
        <DeleteConfirmDialog
          name={`${formatTimeAmPm(deleteTarget.timeMinutes)} — ${deleteTarget.activity}`}
          listPhrase="the daily routine"
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
};

DailySchedule.propTypes = {
  patient: PropTypes.object,
  capturedItems: PropTypes.array,
  /** ZY-10356 — transcript-mode draft preservation key (scoped by
   *  transcriptId). Standalone omits. */
  draftKey: PropTypes.string,
};

export default DailySchedule;
